<template>
  <div class="pdf-page-navigator">
    <div class="navigator-header">
      <h4 class="navigator-title">
        <i class="fas fa-file-pdf"></i>
        {{ $t('annotation.pages') }}
      </h4>
      <span class="page-count">{{ totalPages }} {{ $t('annotation.pages') }}</span>
    </div>

    <div class="page-thumbnails">
      <div
        v-for="page in totalPages"
        :key="page"
        :class="['page-thumbnail', { active: page === currentPage }]"
        @click="$emit('page-change', page)"
      >
        <div class="thumbnail-image">
          <canvas :ref="(el: any) => setThumbnailRef(page, el)" class="thumbnail-canvas"></canvas>
          <div v-if="loadingThumbnails" class="thumbnail-loading">
            <div class="spinner-small"></div>
          </div>
        </div>
        <div class="thumbnail-info">
          <span class="page-number">{{ page }}</span>
          <span v-if="getAnnotationCount(page) > 0" class="annotation-badge">
            <i class="fas fa-comment"></i>
            {{ getAnnotationCount(page) }}
          </span>
        </div>
      </div>
    </div>

    <div class="navigator-controls">
      <button
        class="nav-btn"
        :disabled="currentPage === 1"
        @click="$emit('page-change', currentPage - 1)"
        :title="$t('annotation.previousPage')"
      >
        <i class="fas fa-chevron-up"></i>
      </button>
      <span class="current-page-indicator">
        {{ currentPage }} / {{ totalPages }}
      </span>
      <button
        class="nav-btn"
        :disabled="currentPage === totalPages"
        @click="$emit('page-change', currentPage + 1)"
        :title="$t('annotation.nextPage')"
      >
        <i class="fas fa-chevron-down"></i>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  totalPages: number;
  currentPage: number;
  annotationCounts: Record<number, number>;
  documentUrl: string; // PDF URL to load
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'page-change', page: number): void;
}>();

const { t } = useI18n();

const thumbnailRefs = ref<Record<number, HTMLCanvasElement>>({});
const loadingThumbnails = ref(false);
let pdfDocumentInstance: any = null; // Don't use ref for PDF.js objects

const setThumbnailRef = (page: number, el: any) => {
  if (el) {
    thumbnailRefs.value[page] = el as HTMLCanvasElement;
  }
};

const getAnnotationCount = (page: number): number => {
  return props.annotationCounts[page] || 0;
};

const loadPdfDocument = async () => {
  try {
    const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
    
    GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.mjs',
      import.meta.url
    ).toString();

    const loadingTask = getDocument({
      url: props.documentUrl,
      cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
      cMapPacked: true,
    });
    
    pdfDocumentInstance = await loadingTask.promise;
  } catch (error) {
    console.error('Error loading PDF for thumbnails:', error);
  }
};

const generateThumbnails = async () => {
  if (!pdfDocumentInstance || loadingThumbnails.value) return;

  loadingThumbnails.value = true;
  
  try {
    for (let pageNum = 1; pageNum <= props.totalPages; pageNum++) {
      const canvas = thumbnailRefs.value[pageNum];
      if (!canvas) continue;

      const page = await pdfDocumentInstance.getPage(pageNum);
      const viewport = page.getViewport({ scale: 0.2 });
      
      const context = canvas.getContext('2d');
      if (!context) continue;

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
    }
  } catch (error) {
    console.error('Error generating thumbnails:', error);
  } finally {
    loadingThumbnails.value = false;
  }
};

watch(() => props.documentUrl, async (newUrl: string) => {
  if (newUrl && props.totalPages > 1) {
    await loadPdfDocument();
    setTimeout(() => generateThumbnails(), 100);
  }
}, { immediate: true });

onMounted(async () => {
  if (props.documentUrl && props.totalPages > 1) {
    await loadPdfDocument();
    generateThumbnails();
  }
});

onUnmounted(() => {
  // Cleanup PDF document
  if (pdfDocumentInstance) {
    try {
      pdfDocumentInstance.destroy();
    } catch (e) {
      console.warn('Error destroying PDF document in navigator:', e);
    }
    pdfDocumentInstance = null;
  }
});
</script>

<style scoped>
.pdf-page-navigator {
  width: 200px;
  height: 100%;
  background: var(--surface-secondary);
  border-right: 1px solid var(--border-light);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.navigator-header {
  padding: var(--space-md);
  border-bottom: 1px solid var(--border-light);
  background: white;
}

.navigator-title {
  margin: 0 0 var(--space-xs);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  display: flex;
  align-items: center;
  gap: var(--space-xs);
}

.page-count {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.page-thumbnails {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-sm);
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.page-thumbnail {
  background: white;
  border: 2px solid var(--border-light);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;
}

.page-thumbnail:hover {
  border-color: var(--primary);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.page-thumbnail.active {
  border-color: var(--primary);
  box-shadow: 0 0 0 2px var(--primary-light);
}

.thumbnail-image {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1.414; /* A4 ratio */
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.thumbnail-canvas {
  max-width: 100%;
  max-height: 100%;
  display: block;
}

.thumbnail-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.thumbnail-info {
  padding: var(--space-xs) var(--space-sm);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--surface-secondary);
}

.page-number {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.annotation-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-xs);
  color: var(--primary);
  background: var(--primary-light);
  padding: 2px 6px;
  border-radius: 10px;
}

.navigator-controls {
  padding: var(--space-md);
  border-top: 1px solid var(--border-light);
  background: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
}

.nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--surface-secondary);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s;
}

.nav-btn:hover:not(:disabled) {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.current-page-indicator {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

/* Scrollbar styling */
.page-thumbnails::-webkit-scrollbar {
  width: 8px;
}

.page-thumbnails::-webkit-scrollbar-track {
  background: var(--surface-secondary);
}

.page-thumbnails::-webkit-scrollbar-thumb {
  background: var(--border-medium);
  border-radius: 4px;
}

.page-thumbnails::-webkit-scrollbar-thumb:hover {
  background: var(--text-secondary);
}
</style>
